// DocumentPage.tsx
import {
  useState,
  useEffect,
  use,
  useCallback,
  MutableRefObject,
  useMemo,
  JSX,
} from "react";
import { Document as PDFDocument, Page } from "react-pdf";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { useResizeObserver } from "@wojtekmaj/react-hooks";
import { useTranslation } from "react-i18next";
import { Document } from "@/graphql/__generated__/graphql";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { DocumentContext, DocumentContextType } from "@/context/DocumentProvider";
import { useSearchParams } from "react-router-dom";
import {
  clearUnwantedSearchParams,
  renderBboxHighlight as renderBboxHighlightUtil,
  handleTextSelection as handleTextSelectionUtil,
  BboxHighlight
} from "../utils";
import { ExternalLinkIcon, SearchIcon, RotateCcwIcon, ZoomInIcon, ZoomOutIcon } from "lucide-react";
import { CircleLoader } from "@/components/shared/loaders";
import { cn } from "@/lib/utils";

interface DocumentPageProps {
  pdfDocument: Document | null;
  documents?: Document[];
  onSelectDocument?: (_doc: Document) => void;
  initialPage?: number;
  heightClassName?: string;
  basePdfRender?: boolean;
};

const resizeObserverOptions = {};
const maxWidth = 800;

/**
 * DocumentPage component renders a PDF document with various functionalities such as zooming, text highlighting, and document selection.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.pdfDocument - The PDF document to be displayed.
 * @param {Array} props.documents - The list of documents available for selection.
 * @param {Function} props.onSelectDocument - Callback function to handle document selection.
 * @param {number} props.initialPage - The initial page number to be displayed.
 * @param {string} props.heightClassName - CSS class name for controlling the height of the document container
 * @returns {JSX.Element} The rendered DocumentPage component.
 */
const DocumentPage = (props: DocumentPageProps): JSX.Element => {
  const {
    pdfDocument, documents, onSelectDocument, initialPage, basePdfRender
  } = props;

  // Memoize the options object to prevent unnecessary rerenders
  const options = useMemo(() => ({
    cMapUrl: "/cmaps/",
    standardFontDataUrl: "/standard_fonts/",
  }), []);

  // States
  const [highlightBbox, setHighlightBbox] = useState<BboxHighlight | undefined>(undefined);

  // Get highlight parameters from URL search params
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get("page");
  const bbox_leftParam = searchParams.get("bbox_left");
  const bbox_rightParam = searchParams.get("bbox_right");
  const bbox_topParam = searchParams.get("bbox_top");
  const bbox_bottomParam = searchParams.get("bbox_bottom");
  const bbox_coord_originParam = searchParams.get("bbox_coord_origin");

  // Hooks
  const { t } = useTranslation(["components"]);
  const {
    containerRef,
    setContainerRef,
    containerWidth,
    scale,
    rotate,
    highlightedElements,
    setHighlightedElements,
    pageDimensions,
    setPageDimensions,
    pageRefs,
    onResize,
    setSelectedText,
    shouldClearSelection,
    setShouldClearSelection,
    setScale
  } = use(DocumentContext) as DocumentContextType;

  // States
  const [numPages, setNumPages] = useState<number>(0);
  const [bboxHighlightElement, setBboxHighlightElement] = useState<HTMLElement | null>(null);
  const [isZoomPopoverOpen, setIsZoomPopoverOpen] = useState(false);

  useResizeObserver(containerRef, resizeObserverOptions, onResize);

  // Parse bbox parameters from URL if they exist
  useEffect(() => {
    if (basePdfRender) return;

    // Only set highlight if we have all necessary parameters
    // Check explicitly for non-undefined values rather than just truthy values
    if (pageParam !== null &&
        bbox_leftParam !== null &&
        bbox_rightParam !== null &&
        bbox_topParam !== null &&
        bbox_bottomParam !== null) {

      setHighlightBbox({
        page: parseInt(pageParam, 10),
        bbox_left: parseFloat(bbox_leftParam),
        bbox_right: parseFloat(bbox_rightParam),
        bbox_top: parseFloat(bbox_topParam),
        bbox_bottom: parseFloat(bbox_bottomParam),
        bbox_coord_origin: bbox_coord_originParam || "TOPLEFT"
      });
    } else {
      setHighlightBbox(undefined);

      // Remove any existing highlight element when parameters are cleared
      if (bboxHighlightElement) {
        bboxHighlightElement.remove();
        setBboxHighlightElement(null);
      }

      // Only clear parameters if we don't have the required bbox parameters
      // This avoids accidentally removing parameters during initialization
      if (searchParams.has("file") && searchParams.has("page") &&
          (!searchParams.has("bbox_left") || !searchParams.has("bbox_right") ||
           !searchParams.has("bbox_top") || !searchParams.has("bbox_bottom"))) {
        clearUnwantedSearchParams(searchParams, setSearchParams);
      }
    }
  }, [searchParams, pageParam, bbox_leftParam, bbox_rightParam, bbox_topParam, bbox_bottomParam, bbox_coord_originParam, bboxHighlightElement]);

  useEffect(() => {
    if (initialPage && pageRefs.current[initialPage]) {
      const pageElement = pageRefs.current[initialPage];
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [initialPage, pageRefs.current]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page: any) => {
    if (!pageDimensions) {
      setPageDimensions({ width: page.originalWidth, height: page.originalHeight });
    }
  };

  // *** Toolbar functions ***
  const MIN_SCALE = 0.1;
  const MAX_SCALE = 3.0;
  const DEFAULT_SCALE = 1.0;

  const handleZoomChange = (value: number[]) => {
    setScale(value[0]);
  };

  const resetZoom = () => {
    setScale(DEFAULT_SCALE);
  };

  /**
   * Handles the text selection within the document.
   *
   * This function performs the following steps:
   * 1. Removes the "text-highlight" class from all previously highlighted elements.
   * 2. Clears the list of highlighted elements.
   * 3. Retrieves the current text selection from the window.
   * 4. If there is a text selection, it iterates through the selected ranges and highlights the text by adding the "text-highlight" class to the parent `SPAN` elements.
   * 5. Updates the state with the newly highlighted elements and the selected text.
   * 6. If no text is selected, it clears the selected text state.
   *
   * @returns {void}
   */
  const handleTextSelection = (): void => {
    if (basePdfRender) return;

    handleTextSelectionUtil(
      highlightedElements,
      setHighlightedElements,
      setSelectedText,
      searchParams,
      setSearchParams,
      setHighlightBbox,
      setBboxHighlightElement
    );
  };

  /**
   * Renders a bounding box highlight on the PDF page.
   */
  const renderBboxHighlight = useCallback(() => {
    renderBboxHighlightUtil(
      highlightBbox,
      pageDimensions,
      containerRef,
      pageRefs as MutableRefObject<Record<number, HTMLDivElement>>,
      bboxHighlightElement,
      setBboxHighlightElement
    );
  }, [highlightBbox, pageDimensions, containerRef, pageRefs, bboxHighlightElement, setBboxHighlightElement]);

  // Scroll to the highlighted page
  // Only scroll to the highlighted page on initial render or when page changes
  useEffect(() => {
    // Find the page element where the highlight should be rendered
    const pageElement = pageRefs.current[pageParam ?? 0];
    if (!pageElement) return;

    // Scroll to the highlighted page
    pageElement.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [pageParam, pageRefs]);

  // Apply the highlight when the component mounts or when relevant props change
  useEffect(() => {
    // Add a small delay to ensure the PDF has fully rendered
    const timer = setTimeout(() => {
      if (highlightBbox && pageDimensions) {
        renderBboxHighlight();
      } else if (!highlightBbox && bboxHighlightElement) {
        // Explicitly remove highlight when highlightBbox is cleared
        bboxHighlightElement.remove();
        setBboxHighlightElement(null);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [highlightBbox, pageDimensions, scale, renderBboxHighlight, numPages, bboxHighlightElement]);

  // Clean up highlight on unmount
  useEffect(() => {
    return () => {
      if (bboxHighlightElement) {
        bboxHighlightElement.remove();
      }
    };
  }, [bboxHighlightElement]);

  /**
   * A loading component that displays a centered oval spinner.
   *
   * @returns {JSX.Element} A JSX element containing a div with a centered oval spinner.
   */
  const LoadingComponent = (): JSX.Element => (
    <div className="flex items-center justify-center h-[70vh]">
      <CircleLoader
        visible={true}
        height={80}
        width={80}
        strokeWidth={8}
        ariaLabel="circle-loader"
      />
    </div>
  );

  /**
   * A functional component that displays a message indicating that the PDF is unavailable.
   *
   * @returns {JSX.Element} A JSX element containing a centered message.
   */
  const NoDataComponent = (): JSX.Element => (
    <div className="flex items-center justify-center h-[60vh] w-full">
      <p>{t("documentchat.documentpage.pdfunavailable")}</p>
    </div>
  );

  useEffect(() => {
    if (shouldClearSelection) {
      highlightedElements.forEach((el) => {
        el.classList.remove("text-highlight");
      });
      setHighlightedElements([]);
      setShouldClearSelection(false);
      window.getSelection()?.removeAllRanges();
    }
  }, [shouldClearSelection, highlightedElements, setShouldClearSelection]);

  return (
    <Card className="w-full">
      {pdfDocument ? (
        <>
          <div className="px-6 py-1 flex flex-row gap-4 justify-between items-center bg-white shadow-lg border-b">
            {documents && documents.length > 1 ? (
              <Select
                value={pdfDocument.id.toString()}
                onValueChange={(value) => {
                  const newDocument = documents.find(
                    (doc) => doc.id.toString() === value
                  );
                  if (newDocument && onSelectDocument) {
                    onSelectDocument(newDocument);
                  }
                }}
              >
                <SelectTrigger className="max-w-[350px] 2xl:max-w-[450px] truncate h-[26px] pr-2">
                  <SelectValue placeholder="Select a document" />
                </SelectTrigger>
                <SelectContent>
                  {documents.map((doc) => (
                    <SelectItem key={doc.id} value={doc.id.toString()}>
                      {doc.fileName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm max-w-[300px] 2xl:max-w-[400px] 3xl:max-w-full truncate">
                {pdfDocument.fileName}
              </p>
            )}
            <div className="gap-1 flex items-center">
              <Popover open={isZoomPopoverOpen} onOpenChange={setIsZoomPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="xs" className="py-0.5">
                    <SearchIcon className="icon-xs" />
                    <span className="ml-1">{Math.round(scale * 100)}%</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-60">
                  <div className="flex items-center gap-2">
                    <ZoomOutIcon className="icon-sm text-muted-foreground" />
                    <Slider
                      value={[scale]}
                      onValueChange={handleZoomChange}
                      min={MIN_SCALE}
                      max={MAX_SCALE}
                      step={0.1}
                      className="w-full"
                    />
                    <ZoomInIcon className="icon-sm text-muted-foreground" />
                  </div>
                </PopoverContent>
              </Popover>
              <Button onClick={resetZoom} variant="outline" size="xs" className="py-1">
                <RotateCcwIcon className="icon-xs" />
              </Button>
              <a target="_blank" rel="noreferrer" href={pdfDocument.url}>
                <Button type="button" size="xs" variant="outline" className="py-1">
                  <ExternalLinkIcon className="icon-xs" />
                </Button>
              </a>
            </div>
          </div>

          <div
            className={cn(props.heightClassName ?? "h-[calc(100vh-10rem)]", "overflow-y-scroll justify-center items-center bg-gray-100")}
            ref={setContainerRef}
          >
            <PDFDocument
              file={pdfDocument.url}
              loading={LoadingComponent}
              noData={NoDataComponent}
              error={NoDataComponent}
              onLoadSuccess={onDocumentLoadSuccess}
              className="pdf-document inline-block min-w-full"
              options={options}
            >
              {Array.from(new Array(numPages), (el, index) => (
                <div
                  key={`page_${index + 1}`}
                  ref={(ref) => {
                    if (ref) pageRefs.current[index + 1] = ref;
                  }}
                  className="flex justify-center py-4"
                >
                  <Page
                    onMouseUp={handleTextSelection}
                    className="w-auto"
                    pageNumber={index + 1}
                    loading={LoadingComponent}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    width={containerWidth ? Math.min(containerWidth, maxWidth) : maxWidth}
                    scale={scale}
                    rotate={rotate}
                    onLoadSuccess={index === 0 ? onPageLoadSuccess : undefined}
                    onRenderSuccess={() => {
                      if (initialPage && index + 1 === initialPage) {
                        const pageElement = pageRefs.current[initialPage];
                        if (pageElement) {
                          pageElement.scrollIntoView({ behavior: "smooth" });
                        }
                      }
                    }}
                  />
                </div>
              ))}
            </PDFDocument>
          </div>
        </>
      ) : (
        <NoDataComponent/>
      )}
    </Card>
  );
};

export default DocumentPage;
